import cytoscape from 'cytoscape';
import { Core } from 'cytoscape';

export interface CytoscapeStylesheet {
  selector: string;
  style: Record<string, string | number | boolean>;
}

export interface Node {
  id: string;
  label: string;
  description?: string | null;
  value: string;
  weight: number;
  nodeType: NodeType;
  frequency: number;
  themes: string[];
  keywords: string[];
  isSelected?: boolean;
}

export interface Edge {
  id: string;
  source: string;
  target: string;
  weight: number;
  label?: string;
  virtual?: boolean; // 仮想エッジかどうか
}

export interface NetworkData {
  nodes: Node[];
  edges: Edge[];
}

export type NodeIDs = string[];

export interface NetworkVisualizerProps {
  data: NetworkData;
  onNodeClick?: (node: Node) => void;
  searchedNodeId?: string | null;
  setCyRef?: (cy: cytoscape.Core) => void;
}

export interface NetworkLayoutState {
  topHeight: number;
  isControlPanelExpanded: boolean;
  isInfoPanelVisible: boolean;
  searchedNodeId: string | null;
  selectedNode: Node | null;
  selectedTag: string | null;
  filteredData: NetworkData;
  cytoscapeCoreRefObject: React.MutableRefObject<Core | null>;
}
export interface networkStateProps {
  selectedNode: Node | null;
  searchedNodeId: string | null;
  minEdgeWeight: number;
  setMinEdgeWeight: (value: number) => void;
  minNodeWeight: number;
  setMinNodeWeight: (value: number) => void;
  minNodeFrequency: number;
  setMinNodeFrequency: (value: number) => void;
  currentLayout: string;
  setCurrentLayout: (value: string) => void;
  selectedTag: string | null;
  cytoscapeCoreRefObject: React.MutableRefObject<Core | null>;
  filteredData: NetworkData;
  selectedNodeId: string | null;
  handleNodeClick: (node: Node) => void;
  handleInfoPanelClose: () => void;
  handleTagClick: (tag: string) => void;
  setSearchedNodeId: (id: string | null) => void;
  setSelectedNode: (node: Node | null) => void;
  isInfoPanelVisible: boolean;
  setIsInfoPanelVisible: (value: boolean) => void;
}

export interface UseCytoscapeEventsProps {
  cytoscapeCoreRefObject: React.MutableRefObject<cytoscape.Core | null>;
  onNodeClick?: (node: Node) => void;
  fixedNodes: Set<string>;
  setFixedNodes: React.Dispatch<React.SetStateAction<Set<string>>>;
  selectedNodeId?: string | null;
  isFiltering?: boolean;
}

export interface LayoutOptions {
  name: string;
  fit?: boolean;
  padding?: number;
  animate?: boolean;
  animationDuration?: number;
  [key: string]: unknown;
}

export enum LayoutType {
  COSE = 'cose',
  CONCENTRIC = 'concentric',
  BREADTHFIRST = 'breadthfirst',
  CIRCLE = 'circle',
  GRID = 'grid',
  RANDOM = 'random',
  THEME_CIRCULAR = 'theme-circular',
  PRESET = 'preset',
}
export const DEFAULT_LAYOUT = LayoutType.THEME_CIRCULAR;

export const LAYOUT_PRIORITY: LayoutType[] = [
  LayoutType.CONCENTRIC, // 4重円レイアウト
  LayoutType.BREADTHFIRST, // 階層表示（追加）
  LayoutType.CIRCLE, // 円形（代替レイアウト）
  LayoutType.COSE, // 力学モデル（複雑なネットワーク用）
  LayoutType.GRID, // グリッド（デバッグ用）
  LayoutType.RANDOM, // ランダム（テスト用）
  LayoutType.THEME_CIRCULAR, // テーマ円形（テーマ用）
];

export const LAYOUT_LABELS: Record<LayoutType, string> = {
  [LayoutType.CONCENTRIC]: '同心円',
  [LayoutType.CIRCLE]: '円形',
  [LayoutType.GRID]: 'グリッド',
  [LayoutType.COSE]: 'Cose',
  [LayoutType.RANDOM]: 'ランダム',
  [LayoutType.BREADTHFIRST]: '階層表示',
  [LayoutType.THEME_CIRCULAR]: 'テーマ円形',
  [LayoutType.PRESET]: 'プリセット',
} as const;

// NodeTypeの定義（as constで型安全に）
export const NodeType = {
  ASSIST: 'assist',
  CROSS_ISSUE: 'cross_issue',
  CROSS: 'cross',
  THEME: 'theme',
  SOCIAL_ISSUE: 'social_issue',
  NORMAL: 'normal',
  REGION_ECONOMIC_UNION: 'region/economic_union',
  COUNTRY: 'country',
  ORGANIZATION_TYPE: 'organization_type',
} as const;

export type NodeType = (typeof NodeType)[keyof typeof NodeType];

// NODE_TYPESの定義（NodeTypeをキーに持つオブジェクト）
// それぞれのノードタイプに対するレベルとラベルのvariantを定義
export const NODE_TYPES: Record<NodeType, { level: number; label: string }> = {
  [NodeType.ASSIST]: {
    level: 175,
    label: 'アシストタグ',
  },
  [NodeType.CROSS_ISSUE]: {
    level: 10,
    label: 'クロスイシュータグ',
  },
  [NodeType.CROSS]: {
    level: 75,
    label: 'クロスタグ',
  },
  [NodeType.THEME]: {
    level: 100,
    label: 'テーマタグ',
  },
  [NodeType.SOCIAL_ISSUE]: {
    level: 25,
    label: 'イシュータグ',
  },
  [NodeType.NORMAL]: {
    level: 150,
    label: 'ノーマルタグ',
  },
  [NodeType.REGION_ECONOMIC_UNION]: {
    level: 150,
    label: '地域経済連合タグ',
  },
  [NodeType.COUNTRY]: {
    level: 150,
    label: '国タグ',
  },
  [NodeType.ORGANIZATION_TYPE]: {
    level: 150,
    label: '組織形態タグ',
  },
} as const;

export const getNodeTypeName = (type: NodeType | undefined): string => {
  return NODE_TYPES[type as NodeType]?.label || 'その他トピックタグ';
};

// まず型定義
export type NetworkFilterSettings = {
  minNodeWeight: number;
  minEdgeWeight: number;
  minNodeFrequency: number;
  hideTagsWithoutDocuments: boolean;
  showOnlySocialIssues: boolean;
  nodeTypeFilters: NodeTypeFilterSettings;
};

// ノードタイプフィルターの設定
export type NodeTypeFilterSettings = {
  [key in NodeType]: boolean;
};

// デフォルトはアシストタグ以外取得
export const DEFAULT_NODE_TYPE_FILTERS: NodeTypeFilterSettings = {
  [NodeType.THEME]: true,
  [NodeType.CROSS]: true,
  [NodeType.SOCIAL_ISSUE]: true,
  [NodeType.NORMAL]: true,
  [NodeType.ASSIST]: false,
  [NodeType.CROSS_ISSUE]: true,
  [NodeType.REGION_ECONOMIC_UNION]: false, // backendで除外している
  [NodeType.COUNTRY]: false, // backendで除外している
  [NodeType.ORGANIZATION_TYPE]: false, // backendで除外している
};

// フィルタ設定のデフォルト値の定数
export const DEFAULT_FILTER_SETTINGS: NetworkFilterSettings = {
  minNodeWeight: 1,
  minEdgeWeight: 1,
  minNodeFrequency: 1,
  hideTagsWithoutDocuments: false,
  showOnlySocialIssues: false,
  nodeTypeFilters: DEFAULT_NODE_TYPE_FILTERS,
};

// コントロールパネルを表示するか
export const CONTROL_PANEL_VISIBLE = false;

// ズーム関連の定数
export const ZOOM_STEP = 0.1;
export const MIN_ZOOM = 0.3;
export const MAX_ZOOM = 2.0;
